import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/contexts/ToastContext';
import { measurementsApi } from '@/lib/api';
import { formatDate, formatDateISO } from '@/lib/utils';
import type { Measurement } from '@/types';
import { PlusIcon, ChevronRightIcon, ChevronLeftIcon, TrashIcon } from '@/components/Icons';
import LoadingSpinner from '@/components/LoadingSpinner';
import Modal from '@/components/Modal';

const MEASUREMENT_FIELDS = [
  { key: 'weight', label: 'Вес тела', unit: 'кг' },
  { key: 'chest', label: 'Грудь', unit: 'см' },
  { key: 'waist', label: 'Талия', unit: 'см' },
  { key: 'hips', label: 'Бёдра', unit: 'см' },
  { key: 'neck', label: 'Шея', unit: 'см' },
  { key: 'biceps_left', label: 'Бицепс (Л)', unit: 'см' },
  { key: 'biceps_right', label: 'Бицепс (П)', unit: 'см' },
  { key: 'thigh_left', label: 'Бедро (Л)', unit: 'см' },
  { key: 'thigh_right', label: 'Бедро (П)', unit: 'см' },
] as const;

type MeasurementField = (typeof MEASUREMENT_FIELDS)[number]['key'];

export default function MeasurementsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [showModal, setShowModal] = useState(false);
  const [editingMeasurement, setEditingMeasurement] = useState<Measurement | null>(
    null
  );

  // Form state
  const [date, setDate] = useState(formatDateISO());
  const [values, setValues] = useState<Record<MeasurementField, string>>({
    weight: '',
    chest: '',
    waist: '',
    hips: '',
    neck: '',
    biceps_left: '',
    biceps_right: '',
    thigh_left: '',
    thigh_right: '',
  });
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const { data: measurements = [], isLoading: loading } = useQuery({
    queryKey: ['measurements'],
    queryFn: async () => {
      const res = await measurementsApi.list({ limit: 50 });
      return res.success ? res.data?.items : [];
    },
  });

  const refetchMeasurements = () => {
    queryClient.invalidateQueries({ queryKey: ['measurements'] });
  };

  const openAddModal = () => {
    setEditingMeasurement(null);
    setDate(formatDateISO());
    setValues({
      weight: '',
      chest: '',
      waist: '',
      hips: '',
      neck: '',
      biceps_left: '',
      biceps_right: '',
      thigh_left: '',
      thigh_right: '',
    });
    setNote('');
    setError('');
    setShowModal(true);
  };

  const openEditModal = (measurement: Measurement) => {
    setEditingMeasurement(measurement);
    setDate(measurement.date);
    setValues({
      weight: measurement.weight?.toString() || '',
      chest: measurement.chest?.toString() || '',
      waist: measurement.waist?.toString() || '',
      hips: measurement.hips?.toString() || '',
      neck: measurement.neck?.toString() || '',
      biceps_left: measurement.biceps_left?.toString() || '',
      biceps_right: measurement.biceps_right?.toString() || '',
      thigh_left: measurement.thigh_left?.toString() || '',
      thigh_right: measurement.thigh_right?.toString() || '',
    });
    setNote(measurement.note || '');
    setError('');
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setSaving(true);
    setError('');

    const data: Record<string, unknown> = { date, note: note || undefined };
    for (const field of MEASUREMENT_FIELDS) {
      const val = values[field.key];
      data[field.key] = val ? parseFloat(val) : undefined;
    }

    const res = editingMeasurement
      ? await measurementsApi.update(editingMeasurement.id, data)
      : await measurementsApi.create(data as Omit<Measurement, 'id' | 'user_id' | 'created_at'>);

    if (res.success) {
      refetchMeasurements();
      setShowModal(false);
      showToast(editingMeasurement ? 'Замеры обновлены' : 'Замеры сохранены', 'success');
    } else {
      setError(res.error || 'Не удалось сохранить замеры');
    }

    setSaving(false);
  };

  const handleDelete = async (measurement: Measurement) => {
    if (!confirm('Удалить эти замеры?')) return;

    const res = await measurementsApi.delete(measurement.id);
    if (res.success) {
      refetchMeasurements();
      setShowModal(false);
      showToast('Замеры удалены', 'success');
    } else {
      showToast('Не удалось удалить замеры', 'error');
    }
  };

  if (loading) {
    return (
      <div className="page-container flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="page-container">
      <header className="page-header flex items-center justify-between">
        <div className="flex items-center">
          <button
            onClick={() => navigate(-1)}
            className="p-2 -ml-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <ChevronLeftIcon className="w-5 h-5" />
          </button>
          <h1 className="page-title ml-2">Замеры тела</h1>
        </div>
        <button 
          onClick={openAddModal} 
          className="w-9 h-9 rounded-full bg-primary-600 text-white flex items-center justify-center hover:bg-primary-700 transition-colors shadow-sm"
        >
          <PlusIcon className="w-5 h-5" />
        </button>
      </header>

      <div className="p-4">
        {measurements.length === 0 ? (
          <div className="card">
            <div className="empty-state">
              <p className="mb-4">Замеров пока нет</p>
              <button onClick={openAddModal} className="btn-primary">
                Добавить первые замеры
              </button>
            </div>
          </div>
        ) : (
          <div className="card divide-y divide-gray-100">
            {measurements.map((measurement) => (
              <button
                key={measurement.id}
                onClick={() => openEditModal(measurement)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors text-left"
              >
                <div>
                  <p className="font-medium text-gray-900">
                    {formatDate(measurement.date)}
                  </p>
                  <div className="flex flex-wrap gap-x-3 gap-y-1 text-sm text-gray-500 mt-1">
                    {measurement.weight && (
                      <span>{measurement.weight} кг</span>
                    )}
                    {measurement.chest && (
                      <span>Грудь: {measurement.chest}</span>
                    )}
                    {measurement.waist && (
                      <span>Талия: {measurement.waist}</span>
                    )}
                  </div>
                </div>
                <ChevronRightIcon className="w-5 h-5 text-gray-400" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingMeasurement ? 'Редактировать замеры' : 'Новые замеры'}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 text-sm text-red-700 bg-red-50 rounded-lg">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="date" className="label">
              Дата
            </label>
            <input
              id="date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="input"
              required
            />
          </div>

          {/* Weight */}
          <div>
            <label htmlFor="weight" className="label">
              Вес тела (кг)
            </label>
            <input
              id="weight"
              type="number"
              inputMode="decimal"
              step="0.1"
              min="0"
              value={values.weight}
              onChange={(e) => setValues((prev) => ({ ...prev, weight: e.target.value }))}
              className="input"
              placeholder="0"
            />
          </div>

          {/* Torso section */}
          <div className="pt-3 border-t border-gray-100">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-3">Торс</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="chest" className="label">Грудь (см)</label>
                <input
                  id="chest"
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  min="0"
                  value={values.chest}
                  onChange={(e) => setValues((prev) => ({ ...prev, chest: e.target.value }))}
                  className="input"
                  placeholder="0"
                />
              </div>
              <div>
                <label htmlFor="waist" className="label">Талия (см)</label>
                <input
                  id="waist"
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  min="0"
                  value={values.waist}
                  onChange={(e) => setValues((prev) => ({ ...prev, waist: e.target.value }))}
                  className="input"
                  placeholder="0"
                />
              </div>
              <div>
                <label htmlFor="hips" className="label">Бёдра (см)</label>
                <input
                  id="hips"
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  min="0"
                  value={values.hips}
                  onChange={(e) => setValues((prev) => ({ ...prev, hips: e.target.value }))}
                  className="input"
                  placeholder="0"
                />
              </div>
              <div>
                <label htmlFor="neck" className="label">Шея (см)</label>
                <input
                  id="neck"
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  min="0"
                  value={values.neck}
                  onChange={(e) => setValues((prev) => ({ ...prev, neck: e.target.value }))}
                  className="input"
                  placeholder="0"
                />
              </div>
            </div>
          </div>

          {/* Arms section */}
          <div className="pt-3 border-t border-gray-100">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-3">Руки</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="biceps_left" className="label">Бицепс Л (см)</label>
                <input
                  id="biceps_left"
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  min="0"
                  value={values.biceps_left}
                  onChange={(e) => setValues((prev) => ({ ...prev, biceps_left: e.target.value }))}
                  className="input"
                  placeholder="0"
                />
              </div>
              <div>
                <label htmlFor="biceps_right" className="label">Бицепс П (см)</label>
                <input
                  id="biceps_right"
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  min="0"
                  value={values.biceps_right}
                  onChange={(e) => setValues((prev) => ({ ...prev, biceps_right: e.target.value }))}
                  className="input"
                  placeholder="0"
                />
              </div>
            </div>
          </div>

          {/* Legs section */}
          <div className="pt-3 border-t border-gray-100">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-3">Ноги</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="thigh_left" className="label">Бедро Л (см)</label>
                <input
                  id="thigh_left"
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  min="0"
                  value={values.thigh_left}
                  onChange={(e) => setValues((prev) => ({ ...prev, thigh_left: e.target.value }))}
                  className="input"
                  placeholder="0"
                />
              </div>
              <div>
                <label htmlFor="thigh_right" className="label">Бедро П (см)</label>
                <input
                  id="thigh_right"
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  min="0"
                  value={values.thigh_right}
                  onChange={(e) => setValues((prev) => ({ ...prev, thigh_right: e.target.value }))}
                  className="input"
                  placeholder="0"
                />
              </div>
            </div>
          </div>

          <div>
            <label htmlFor="note" className="label">
              Заметка
            </label>
            <textarea
              id="note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="input"
              rows={2}
              placeholder="Комментарий..."
            />
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setShowModal(false)}
              className="btn-secondary flex-1"
            >
              Отмена
            </button>
            <button type="submit" disabled={saving} className="btn-primary flex-1">
              {saving ? <LoadingSpinner size="sm" className="text-white" /> : 'Сохранить'}
            </button>
          </div>

          {editingMeasurement && (
            <button
              type="button"
              onClick={() => handleDelete(editingMeasurement)}
              className="btn-danger w-full"
            >
              <TrashIcon className="w-4 h-4 mr-2" />
              Удалить замеры
            </button>
          )}
        </form>
      </Modal>
    </div>
  );
}
